import { BasePet, CatPet, DogPet, Pet } from 'api/pets';
import { PetType } from 'constants/pet-type';
import { CatProfile, DogProfile, PetProfile, isCatProfile, isDogProfile } from 'types/pet-profile';
import { BreedSegmentationIds, ProteinTagValues } from 'constants/segmentation';
import { WetFoodTagValues } from 'interfaces/api-input/input-product-type';
import { normalizeProteinPreferences } from './protein-preferences';

interface GeneratePetProfileArgs {
  petProfile: PetProfile;
  customerId: string;
}

/**
 * Helper function for toPayloadByPetProfile
 */
export function toPayloadBase({
  petProfile,
  customerId,
}: GeneratePetProfileArgs): BasePet & { id: string; customer_id: string } {
  return {
    id: petProfile.id,
    customer_id: customerId,
    pet_type: petProfile.petType,
    name: petProfile.name || '',
    age: petProfile.selectedAge,
    needs: petProfile.selectedNeeds,
    allergies: petProfile.selectedAllergies,
    food_types: petProfile.selectedFoodTypes,
    weight: petProfile.selectedWeight,
    date_of_birth: petProfile.dateOfBirth,
    body_condition: petProfile.bodyCondition,
    neutered: petProfile.neutered,
    activity_level: petProfile.activityLevel,
    has_special_needs: petProfile.hasSpecialNeeds,
  };
}

type DogPayload = BasePet & {
  id: string;
  customer_id: string;
  pet_type: 'dog';
  breed?: BreedSegmentationIds;
};

type CatPayload = BasePet & {
  id: string;
  customer_id: string;
  pet_type: 'cat';
  wet_food_tags: WetFoodTagValues[];
  protein_tags: ProteinTagValues[];
  disliked_protein_tags: ProteinTagValues[];
};

interface GenerateDogPetProfileArgs {
  petProfile: DogProfile;
  customerId: string;
}

interface GenerateCatPetProfileArgs {
  petProfile: CatProfile;
  customerId: string;
}

/**
 * It converts petProfile into base payload for the requests such as CreatePetPayload or UpdatePetPayload
 */
export function toPayloadByPetProfile(args: GenerateDogPetProfileArgs): DogPayload;
export function toPayloadByPetProfile(args: GenerateCatPetProfileArgs): CatPayload;
export function toPayloadByPetProfile(args: GeneratePetProfileArgs): DogPayload | CatPayload;
export function toPayloadByPetProfile({
  petProfile,
  customerId,
}: GeneratePetProfileArgs): DogPayload | CatPayload {
  const base = toPayloadBase({ petProfile, customerId });

  if (isDogProfile(petProfile)) {
    return {
      ...base,
      pet_type: 'dog',
      breed: petProfile.selectedBreed,
    };
  }

  if (isCatProfile(petProfile)) {
    const proteinPreferences = normalizeProteinPreferences({
      liked: petProfile.selectedTastePreferences,
      disliked: petProfile.selectedDislikedProteins,
    });

    return {
      ...base,
      pet_type: 'cat',
      wet_food_tags: petProfile.selectedWetFoodTypes || [],
      protein_tags: proteinPreferences.liked,
      disliked_protein_tags: proteinPreferences.disliked,
    };
  }

  throw new Error('Invalid profile type');
}

/**
 * Overload type for fromPetToProfile
 */
export function fromPetToProfile(pet: DogPet): DogProfile;
export function fromPetToProfile(pet: CatPet): CatProfile;
export function fromPetToProfile(pet: Pet): PetProfile;
/**
 * It converts Pet to petProfile.
 * It returns the CatProfile or DogProfile regarding the pet_type value, or it returns PetProfile in case of Pet type
 */
export function fromPetToProfile(pet: Pet): PetProfile {
  const base = {
    id: pet.id,
    name: pet.name || '',
    petType: pet.pet_type,
    selectedAge: pet.age ?? undefined,
    selectedAllergies: pet.allergies,
    selectedFoodTypes: pet.food_types,
    selectedNeeds: pet.needs,
    selectedWeight: pet.weight,
    updatedAt: pet.updated_at,
    lastTouched: undefined,
    dateOfBirth: pet.date_of_birth ?? undefined,
    bodyCondition: pet.body_condition ?? undefined,
    neutered: pet.neutered ?? undefined,
    activityLevel: pet.activity_level ?? undefined,
    hasSpecialNeeds: pet.has_special_needs ?? undefined,
    kcalUpdatedAt: pet.kcal_updated_at,
    dailyKcal: pet.daily_kcal,
  };

  if (pet.pet_type === PetType.Dog) {
    return {
      ...base,
      petType: pet.pet_type,
      selectedBreed: pet.breed ?? undefined,
    };
  }

  if (pet.pet_type === PetType.Cat) {
    // Omitted, null, malformed, and unknown values normalize away rather than
    // entering persisted profile state.
    const proteinPreferences = normalizeProteinPreferences({
      liked: pet.protein_tags,
      disliked: pet.disliked_protein_tags,
    });

    return {
      ...base,
      petType: pet.pet_type,
      selectedWetFoodTypes: pet.wet_food_tags || [],
      selectedTastePreferences: proteinPreferences.liked,
      selectedDislikedProteins: proteinPreferences.disliked,
    };
  }

  throw new Error('Invalid profile type');
}

/**
 * Converts a PetProfile to a full Pet object for local storage (NC users)
 * This reuses toPayloadByPetProfile to avoid code duplication
 */
export const profileToPet = ({
  profile,
  customerId,
}: {
  profile: PetProfile;
  customerId: string;
}): Pet => {
  return {
    ...toPayloadByPetProfile({ petProfile: profile, customerId }),
    id: profile.id,
    updated_at: profile.updatedAt || new Date().toISOString(),
    has_special_needs: profile.hasSpecialNeeds,
    kcal_updated_at: profile.kcalUpdatedAt,
    daily_kcal: profile.dailyKcal,
  };
};

/**
 * We use empty string on the FE side, but backend returns us null if the value was empty
 * TODO: we should agree with BE on schema what data types do we have on response
 */
export const isNameMatch = ({ apiPet, localPet }: { apiPet: Pet; localPet: Pet }): boolean => {
  return (
    apiPet.name === localPet.name ||
    (apiPet.name === null && localPet.name === '') ||
    (apiPet.name === '' && localPet.name === null) // should not be possible but we covered it up
  );
};
